import type { FC } from 'react';
import { useState } from 'react';
import type { LibraryBlockTypeForListPage } from '@ContractBuilder/types';

import { useBlockLibraryTable } from '../hooks/use-block-library-table';
import type { SelectedLibraryBlockSummary } from '../types';
import { toSelectedLibraryBlockSummary } from '../types';
import { BlockLibraryListSuccess, BlocksLibraryLoading } from '../views';

interface BlockLibraryCreatorListControllerProps {
  onInsertBlock: (blockLibraryId: string, block: LibraryBlockTypeForListPage | null) => Promise<void>;
  onInsertSelectedBlocks: (blockLibraryIds: string[]) => Promise<boolean>;
  type: 'block' | 'clause';
}

export const BlockLibraryCreatorListController: FC<BlockLibraryCreatorListControllerProps> = ({
  onInsertBlock,
  onInsertSelectedBlocks,
  type,
}) => {
  const [isSelectionExpanded, setIsSelectionExpanded] = useState(true);

  const [previewBlockId, setPreviewBlockId] = useState<string | undefined>(undefined);
  const onPreviewToggle = (blockId: string) =>
    setPreviewBlockId((current) => {
      const next = current === blockId ? undefined : blockId;
      if (next) {
        // Opening a preview takes the same space the selection panel would use — collapse it.
        setIsSelectionExpanded(false);
      }
      return next;
    });

  const [selectedBlocks, setSelectedBlocks] = useState<Map<string, SelectedLibraryBlockSummary>>(new Map());
  const onSelectToggle = (block: LibraryBlockTypeForListPage) =>
    setSelectedBlocks((current) => {
      const next = new Map(current);
      if (next.has(block.id)) {
        next.delete(block.id);
      } else {
        next.set(block.id, toSelectedLibraryBlockSummary(block));
        setIsSelectionExpanded(true);
      }
      return next;
    });
  const onRemoveFromSelection = (blockId: string) =>
    setSelectedBlocks((current) => {
      const next = new Map(current);
      next.delete(blockId);
      return next;
    });
  const onClearSelection = () => setSelectedBlocks(new Map());
  const onToggleSelectionExpanded = () => setIsSelectionExpanded((current) => !current);
  const onInsertAll = async () => {
    const inserted = await onInsertSelectedBlocks(Array.from(selectedBlocks.keys()));
    if (inserted) {
      onClearSelection();
    }
  };

  const {
    table,
    isClause,
    isFetching,
    columnFilters,
    globalFilter,
    highlightedBlocks,
    total,
    infiniteLoaderRef,
    isGroupByParent,
    setIsGroupByParent,
    onSearchApply,
    handleLoadMoreItems,
  } = useBlockLibraryTable(type);

  if (isFetching) {
    return <BlocksLibraryLoading />;
  }

  return (
    <BlockLibraryListSuccess
      blocks={highlightedBlocks}
      count={highlightedBlocks.length}
      filters={columnFilters}
      listRef={infiniteLoaderRef}
      onInsert={onInsertBlock}
      onSearch={onSearchApply}
      searchText={globalFilter}
      total={total}
      onLoadMoreItems={handleLoadMoreItems}
      isClause={isClause}
      isFetching={isFetching}
      table={table}
      isGroupByParent={isGroupByParent}
      setIsGroupByParent={setIsGroupByParent}
      previewBlockId={previewBlockId}
      onPreviewToggle={onPreviewToggle}
      selectedBlocks={selectedBlocks}
      onSelectToggle={onSelectToggle}
      isSelectionExpanded={isSelectionExpanded}
      onToggleSelectionExpanded={onToggleSelectionExpanded}
      onClearSelection={onClearSelection}
      onRemoveFromSelection={onRemoveFromSelection}
      onInsertAll={onInsertAll}
    />
  );
};
